/**
 * Contract detail — info + jobs do contrato.
 */
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { DetailCard, DetailLayout, KeyValue } from "@/components/detail-layout";
import { EntityActions } from "@/components/entity-actions";
import { ContractTypePill, JobStatusPill } from "@/components/status-pill";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { clientsApi, contractsApi } from "@/lib/api-cadastros";
import { jobsApi } from "@/lib/api-jobs";
import { formatCurrency, formatDate, formatRelative } from "@/lib/format";

export function ContractDetailPage() {
  const { id = "" } = useParams();
  const contract = useQuery({ queryKey: ["contract", id], queryFn: () => contractsApi.get(id), enabled: !!id });
  const client = useQuery({
    queryKey: ["contract-client", contract.data?.client_id],
    queryFn: () => clientsApi.get(contract.data!.client_id),
    enabled: !!contract.data?.client_id,
  });
  const jobs = useQuery({
    queryKey: ["contract-jobs", id],
    queryFn: () => jobsApi.list({ contract_id: id, size: 100 }),
    enabled: !!id,
  });

  if (!contract.data) return <div className="text-muted-foreground py-10">Carregando…</div>;
  const c = contract.data;
  return (
    <DetailLayout
      backTo="/contracts"
      eyebrow="Contrato"
      visual={
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-500/10">
          <FileText className="h-6 w-6" />
        </div>
      }
      title={c.name}
      subtitle={client.data?.trade_name ?? "—"}
      badges={
        <>
          <ContractTypePill type={c.type} />
          {c.active ? <Badge variant="success" dot>Ativo</Badge> : <Badge variant="muted" dot>Encerrado</Badge>}
          {c.sla_hours && <Badge variant="info">SLA {c.sla_hours}h</Badge>}
        </>
      }
      actions={
        <EntityActions
          onDelete={() => contractsApi.remove(id)}
          redirectAfterDelete="/contracts"
          invalidateQueries={["contracts"]}
          resourceLabel="contrato"
          hideEdit
        />
      }
    >
      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="jobs">Chamados ({jobs.data?.total ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailCard title="Período">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <KeyValue label="Início" value={formatDate(c.starts_at)} />
                <KeyValue label="Fim" value={formatDate(c.ends_at)} />
              </div>
            </DetailCard>
            <DetailCard title="Comercial">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <KeyValue label="Tipo" value={c.type} />
                <KeyValue label="SLA" value={c.sla_hours ? `${c.sla_hours} horas` : "—"} />
                {c.monthly_credit_limit && (
                  <>
                    <KeyValue label="Crédito mensal" value={formatCurrency(c.monthly_credit_limit)} />
                    <KeyValue label="Usado no mês" value={formatCurrency(c.monthly_credit_used)} />
                  </>
                )}
              </div>
            </DetailCard>
            {c.notes && (
              <DetailCard title="Observações" className="md:col-span-2">
                <p className="text-sm whitespace-pre-wrap">{c.notes}</p>
              </DetailCard>
            )}
          </div>
        </TabsContent>

        <TabsContent value="jobs">
          {jobs.data && jobs.data.items.length > 0 ? (
            <DetailCard>
              <div className="divide-y divide-slate-200/40 dark:divide-white/5 -m-6">
                {jobs.data.items.map((j) => (
                  <Link
                    key={j.id}
                    to={`/jobs/${j.id}`}
                    className="flex items-center justify-between gap-4 px-6 py-3.5 hover:bg-slate-50/60 dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-xs text-muted-foreground">{j.code}</span>
                      <p className="font-medium truncate">{j.title}</p>
                      <p className="text-xs text-muted-foreground">{formatRelative(j.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold tabular-nums">{formatCurrency(j.price)}</span>
                      <JobStatusPill status={j.status} />
                    </div>
                  </Link>
                ))}
              </div>
            </DetailCard>
          ) : (
            <EmptyState title="Sem chamados neste contrato" />
          )}
        </TabsContent>
      </Tabs>
    </DetailLayout>
  );
}
